import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ContactService, ExtendedContact } from '../services/contact.service';
import {
  Contact,
  ContactFilterInput,
} from '../../generated/eveportal-api/graphql';
import {
  auditTime,
  debounceTime,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  Subject,
  switchMap,
  takeUntil,
  tap,
  distinctUntilChanged,
  shareReplay,
  startWith,
} from 'rxjs';
import { SipService } from '../services/sip.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { TokenService } from '../services/token.service';
import { EveCall } from '../models/eve-call';
import { ConferenceService } from '../services/conference.service';

@Component({
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.css'],
})
export class ContactsComponent implements OnInit, OnDestroy {
  contacts: ExtendedContact[] = [];
  searchTerm = '';
  totalCount = 0;

  private readonly chunkSize = 50; // how many to fetch per API call
  private readonly preload = 20; // how many rows from end before prefetch
  loading = false;
  private hasMore = true;

  @ViewChild(CdkVirtualScrollViewport)
  private viewport!: CdkVirtualScrollViewport;

  private searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  scroll$ = new Subject<number>();

  private endUserOrganisationNode =
    this.tokenService.getJwtTokenPayload()?.EndUserOrganisationNode;

  // Optimized trackBy function
  trackByContactId = (_: number, c: ExtendedContact) => c.contactId;

  // Cache for filter to avoid recreating objects
  private filterCache = new Map<string, ContactFilterInput>();
  constructor(
    private contactService: ContactService,
    private sipService: SipService,
    private conferenceService: ConferenceService,
    private tokenService: TokenService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Optimized search pipeline with better caching and deduplication
    this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(), // Prevent duplicate searches
        takeUntil(this.destroy$),
        tap(() => {
          // Reset state for new search
          this.contacts = [];
          this.totalCount = 0;
          this.hasMore = true;
          setTimeout(() => this.viewport?.scrollToIndex(0), 0);
        }),
        switchMap((term) => this.loadContacts$(false, term)),
        shareReplay(1) // Cache the last result
      )
      .subscribe();

    // Optimized scroll pipeline with better throttling
    this.scroll$
      .pipe(
        auditTime(50), // ~20fps throttling
        distinctUntilChanged(), // Prevent duplicate scroll events
        filter(() => !this.loading && this.hasMore),
        filter(() => this.shouldLoadMore()),
        debounceTime(100), // Wait for scroll to settle
        exhaustMap(() => this.loadMoreContacts()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  onSearch(): void {
    this.searchTerm$.next(this.searchTerm);
  }

  onScrolledIndexChange(index: number): void {
    this.scroll$.next(index);
  }

  // Helper method to determine if more contacts should be loaded
  private shouldLoadMore(): boolean {
    const { end } = this.viewport.getRenderedRange();
    const dataLength = this.viewport.getDataLength();
    return dataLength - end <= this.preload;
  }

  // Helper method to load more contacts with proper loading state
  private loadMoreContacts() {
    this.loading = true;
    return this.loadContacts$(true, this.searchTerm).pipe(
      finalize(() => (this.loading = false))
    );
  }

  private loadContacts$(append = false, term = '') {
    const skip = append ? this.contacts.length : 0;
    const take = this.chunkSize;
    const t = (term ?? '').trim(); // Use the term parameter instead of this.searchTerm

    const scopeFilter: ContactFilterInput = {
      and: [
        {
          or: [
            { number: { neq: null } },
            { internalNumber: { neq: null } },
            { phoneNumber: { neq: null } },
          ],
        },
        {
          or: [
            {
              organisationNode: {
                isDescendantOf: getCompanyNode(this.endUserOrganisationNode),
              },
            },
            { organisationNode: { eq: null } },
          ],
        },
      ],
    };

    const filter: ContactFilterInput = t
      ? {
          and: [
            scopeFilter,
            {
              or: [
                { firstName: { contains: t } },
                { surname: { contains: t } },
                { eveUserName: { contains: t } },
                { phoneNumber: { contains: t } },
              ],
            },
          ],
        }
      : scopeFilter;

    // ✅ capture current pixel offset BEFORE changing data
    const offsetBefore = this.viewport?.measureScrollOffset('top') ?? 0;

    return this.contactService.getContacts(filter, skip, take).pipe(
      tap((res) => {
        this.totalCount = res.totalCount ?? this.totalCount;

        const seen = new Set(this.contacts.map((c) => c.contactId));
        const incoming: ExtendedContact[] = (res.items ?? [])
          .filter((c) => !seen.has(c.contactId))
          .map((c) => ({ ...c, avatarUrl: 'assets/eveuser-avatar.png' }));

        // Apply data change, then restore pixel offset
        requestAnimationFrame(() => {
          this.contacts = append ? [...this.contacts, ...incoming] : incoming;

          // ✅ restore the exact pixel offset; no index math
          this.viewport?.scrollToOffset(offsetBefore, 'auto');
          this.viewport?.checkViewportSize();

          const received = incoming.length;
          this.hasMore = skip + received < this.totalCount;
        });
      }),
      switchMap(() => EMPTY)
    );
  }

  onAddContact() {
    console.log('Add contact clicked');
  }

  toggleFavourite(contact: Contact) {
    contact.favourite = !contact.favourite;
  }

  // async onCall(contact: Contact) {
  //   try {
  //     console.log('Calling', contact);
  //     await this.holdAllUnHeldCalls(); // Hold all other unheld calls

  //     // find active conference and deactivate it
  //     const activeConference = this.conferenceService.getActiveConference();
  //     if (activeConference) {
  //       await this.conferenceService.deactivateCurrentConference();
  //     }

  //     await this.sipService.makeCall(
  //       contact.phoneNumber ?? contact.eveUserName ?? ''
  //     );
  //   } catch (error: any) {
  //     const errorMessage =
  //       'Failed to make call: ' +
  //       (error instanceof Error ? error.message : 'Unknown error');
  //     this.snack.open(errorMessage, 'Dismiss');
  //   }
  // }

  onChat(contact: Contact) {
    console.log('Chatting with', contact);
  }

  onVideoCall(contact: Contact) {
    console.log('Video calling', contact);
  }

  onDelete(contact: Contact) {
    console.log('Deleting', contact);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear caches to prevent memory leaks
    this.filterCache.clear();
  }

  private getAllUnHoldCalls(): EveCall[] | null {
    const activeCalls = this.sipService.getActiveCalls();
    if (!activeCalls || activeCalls.length === 0) return null;
    return activeCalls.filter(
      (c) => c.isOnHold === false && c.isInConference === false
    );
  }
  private async holdAllUnHeldCalls(): Promise<void[]> {
    const unheldCalls = this.getAllUnHoldCalls();
    if (!unheldCalls || unheldCalls.length === 0) return Promise.resolve([]);
    return await Promise.all(unheldCalls.map((c) => c.holdCall()));
  }
}
export const getCompanyNode = (
  organisationNode: String | null
): String | null => {
  if (organisationNode == null) return null;
  const organisationNodeParts = organisationNode.split('/');
  if (organisationNodeParts.length == 6) return organisationNode;

  return `/${organisationNodeParts[1]}/${organisationNodeParts[2]}/${organisationNodeParts[3]}/${organisationNodeParts[4]}/`;
};
